import { useCallback, useEffect, useMemo, useState } from "react";

import { automationService } from "../../automation/services/automationService";
import type { AutomationContext } from "../../automation/types";

export interface UseAutomationChannelImpactParams {
  organizationId?: string;
  practitionerId?: string;
  wabaId?: string;
}

export interface UseAutomationChannelImpactResult {
  automationContext: AutomationContext | null;
  hasConfiguredAutomations: boolean;
  isCheckingAutomations: boolean;
  refreshAutomationConfigStatus: () => Promise<void>;
  removeAllAutomationConfigs: () => Promise<void>;
}

/**
 * Centraliza leitura e reset das automações personalizadas para avisos
 * ao trocar o canal WhatsApp (Meta ↔ Fluxmed).
 */
export function useAutomationChannelImpact(
  params: UseAutomationChannelImpactParams,
): UseAutomationChannelImpactResult {
  const organizationId = params.organizationId?.trim() || "";
  const practitionerId = params.practitionerId?.trim() || undefined;
  const wabaId = params.wabaId?.trim() || undefined;

  const automationContext = useMemo<AutomationContext | null>(() => {
    if (!organizationId) return null;

    return {
      organizationId,
      practitionerId,
      whatsappBusinessAccountId: wabaId,
    };
  }, [organizationId, practitionerId, wabaId]);

  const [hasConfiguredAutomations, setHasConfiguredAutomations] =
    useState(false);
  const [isCheckingAutomations, setIsCheckingAutomations] = useState(false);

  const refreshAutomationConfigStatus = useCallback(async () => {
    if (!automationContext) {
      setHasConfiguredAutomations(false);
      return;
    }

    setIsCheckingAutomations(true);
    try {
      const hasConfigs = await automationService.hasAutomationConfigsByContext({
        context: automationContext,
      });
      setHasConfiguredAutomations(hasConfigs);
    } finally {
      setIsCheckingAutomations(false);
    }
  }, [automationContext]);

  const removeAllAutomationConfigs = useCallback(async () => {
    if (!automationContext) return;

    await automationService.removeAllConfigsByContext({
      context: automationContext,
    });
    setHasConfiguredAutomations(false);
  }, [automationContext]);

  useEffect(() => {
    void refreshAutomationConfigStatus();
  }, [refreshAutomationConfigStatus]);

  return {
    automationContext,
    hasConfiguredAutomations,
    isCheckingAutomations,
    refreshAutomationConfigStatus,
    removeAllAutomationConfigs,
  };
}
